import axiosInstance from "@/axiosConfig";
import { getApiUrl } from "@/shared/utils/api";
import { CHAT_ENDPOINTS } from "../configs/chat.config";
import type {
  ChatRoomsResponse,
  ChatRoomResponse,
  ChatMessagesResponse,
  ChatMessageResponse,
  ChatUsersResponse,
} from "../types/chat.types";

export class ChatService {
  // Rooms
  static async getRooms(): Promise<ChatRoomsResponse> {
    const { data } = await axiosInstance.get<ChatRoomsResponse>(
      getApiUrl(CHAT_ENDPOINTS.ROOMS),
    );
    return data;
  }

  static async getRoom(id: string): Promise<ChatRoomResponse> {
    const { data } = await axiosInstance.get<ChatRoomResponse>(
      getApiUrl(CHAT_ENDPOINTS.ROOM(id)),
    );
    return data;
  }

  static async createRoom(params: {
    representative_id?: string
    employee_id?: string
    member_ids?: string[]
    name?: string
  }): Promise<ChatRoomResponse> {
    const { data } = await axiosInstance.post<ChatRoomResponse>(
      getApiUrl(CHAT_ENDPOINTS.ROOMS),
      { room: params },
    );
    return data;
  }

  static async updateRoom(id: string, name: string): Promise<ChatRoomResponse> {
    const { data } = await axiosInstance.patch<ChatRoomResponse>(
      getApiUrl(CHAT_ENDPOINTS.ROOM(id)),
      { room: { name } },
    );
    return data;
  }

  static async markAsRead(id: string): Promise<ChatRoomResponse> {
    const { data } = await axiosInstance.patch<ChatRoomResponse>(
      getApiUrl(CHAT_ENDPOINTS.MARK_AS_READ(id)),
    );
    return data;
  }

  static async addMembers(roomId: string, memberIds: string[]): Promise<ChatRoomResponse> {
    const { data } = await axiosInstance.post<ChatRoomResponse>(
      getApiUrl(CHAT_ENDPOINTS.ADD_MEMBERS(roomId)),
      { member_ids: memberIds },
    );
    return data;
  }

  static async removeMember(roomId: string, memberId: string): Promise<ChatRoomResponse> {
    const { data } = await axiosInstance.delete<ChatRoomResponse>(
      getApiUrl(CHAT_ENDPOINTS.REMOVE_MEMBER(roomId)),
      { data: { member_id: memberId } },
    );
    return data;
  }

  static async leaveRoom(roomId: string): Promise<void> {
    await axiosInstance.delete(getApiUrl(CHAT_ENDPOINTS.LEAVE(roomId)));
  }

  // Messages
  static async getMessages(roomId: string, limit?: number, offset?: number): Promise<ChatMessagesResponse> {
    const { data } = await axiosInstance.get<ChatMessagesResponse>(
      getApiUrl(CHAT_ENDPOINTS.MESSAGES(roomId)),
      { params: { limit, offset } },
    );
    return data;
  }

  static async sendMessage(
    roomId: string,
    content: string,
    attachments?: File[],
  ): Promise<ChatMessageResponse> {
    if (attachments?.length) {
      const formData = new FormData();
      formData.append("message[content]", content);
      attachments.forEach((file) => {
        formData.append("message[attachments][]", file);
      });

      const { data } = await axiosInstance.post<ChatMessageResponse>(
        getApiUrl(CHAT_ENDPOINTS.MESSAGES(roomId)),
        formData,
        { headers: { "Content-Type": "multipart/form-data" } },
      );
      return data;
    }

    const { data } = await axiosInstance.post<ChatMessageResponse>(
      getApiUrl(CHAT_ENDPOINTS.MESSAGES(roomId)),
      { message: { content } },
    );
    return data;
  }

  static async updateMessage(
    roomId: string,
    messageId: string,
    content: string,
  ): Promise<ChatMessageResponse> {
    const { data } = await axiosInstance.patch<ChatMessageResponse>(
      getApiUrl(CHAT_ENDPOINTS.MESSAGE(roomId, messageId)),
      { message: { content } },
    );
    return data;
  }

  static async deleteMessage(roomId: string, messageId: string, mode: "for_me" | "for_everyone" = "for_me"): Promise<void> {
    await axiosInstance.delete(getApiUrl(CHAT_ENDPOINTS.MESSAGE(roomId, messageId)), { params: { mode } });
  }

  static async searchMessages(roomId: string, query: string): Promise<ChatMessagesResponse> {
    const { data } = await axiosInstance.get<ChatMessagesResponse>(
      getApiUrl(CHAT_ENDPOINTS.SEARCH_MESSAGES(roomId)),
      { params: { q: query } },
    );
    return data;
  }

  // Users
  static async getUsers(params: { q?: string, page?: number, per_page?: number } = {}): Promise<ChatUsersResponse> {
    const { data } = await axiosInstance.get<ChatUsersResponse>(
      getApiUrl(CHAT_ENDPOINTS.USERS),
      { params },
    );
    return data;
  }
}
